// Google truncates snippets around 155 characters, so anything past this never
// renders in search results.
export const META_DESCRIPTION_MAX_LENGTH = 155;

// Google shows roughly 60 characters of a title before truncating (and often
// rewrites titles that run longer).
export const SEO_TITLE_MAX_LENGTH = 60;

const ELLIPSIS = '…';

const collapseWhitespace = (text: string): string =>
  text.replace(/\s+/g, ' ').trim();

/**
 * Trim text to a maximum length without cutting a word in half.
 */
export const truncateAtWordBoundary = (
  text: string,
  maxLength: number = META_DESCRIPTION_MAX_LENGTH
): string => {
  const cleaned = collapseWhitespace(text);
  if (cleaned.length <= maxLength) {
    return cleaned;
  }

  const clipped = cleaned.slice(0, maxLength - ELLIPSIS.length);
  const lastSpace = clipped.lastIndexOf(' ');
  const truncated = lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped;

  // Don't leave dangling punctuation in front of the ellipsis
  return `${truncated.replace(/[\s,;:.!?-]+$/, '')}${ELLIPSIS}`;
};

/**
 * The share of letters that must be uppercase before a subject counts as
 * shouting. Below this, mixed casing is assumed intentional and left alone.
 */
const UPPERCASE_SUBJECT_THRESHOLD = 0.8;

// Tokens of 2–5 uppercase letters read as acronyms (DOJ, KOSA, ICE) and keep
// their casing when a shouting subject is normalized.
const ACRONYM_PATTERN = /^[A-Z]{2,5}$/;

// In an all-caps subject every short word matches ACRONYM_PATTERN, so ordinary
// English words that show up in petition subjects are listed here to be
// title-cased instead of preserved. An unlisted false positive just keeps the
// caps the author typed. 'US' is deliberately absent: in civic subjects
// caps-US is usually the country (US TROOPS), not the pronoun.
const COMMON_SHORT_WORDS = new Set([
  'AN',
  'AS',
  'AT',
  'BE',
  'BY',
  'DO',
  'GO',
  'IF',
  'IN',
  'IS',
  'IT',
  'MY',
  'NO',
  'OF',
  'ON',
  'OR',
  'SO',
  'TO',
  'UP',
  'WE',
  'ACT',
  'ALL',
  'AND',
  'ANY',
  'ARE',
  'ASK',
  'BAN',
  'BIG',
  'CAN',
  'CUT',
  'DAY',
  'END',
  'FOR',
  'GET',
  'GUN',
  'HAS',
  'HER',
  'HIM',
  'HIS',
  'HOW',
  'ITS',
  'JOB',
  'LAW',
  'LET',
  'MAY',
  'NEW',
  'NOT',
  'NOW',
  'OFF',
  'OLD',
  'ONE',
  'OUR',
  'OUT',
  'OWN',
  'PAY',
  'PER',
  'PUT',
  'SAY',
  'SEE',
  'SET',
  'TAX',
  'THE',
  'TOO',
  'TWO',
  'USE',
  'WAR',
  'WAS',
  'WAY',
  'WHO',
  'WHY',
  'WIN',
  'YES',
  'YET',
  'YOU',
  'BACK',
  'BANS',
  'BILL',
  'CALL',
  'CARE',
  'CITY',
  'DEAR',
  'DOES',
  'DOWN',
  'EACH',
  'EVEN',
  'FAIR',
  'FIRE',
  'FREE',
  'FROM',
  'FULL',
  'FUND',
  'GIVE',
  'GOOD',
  'GUNS',
  'HAVE',
  'HELP',
  'HERE',
  'HIGH',
  'HOME',
  'JOBS',
  'JOIN',
  'JUST',
  'KEEP',
  'KIDS',
  'KNOW',
  'LAND',
  'LAWS',
  'LIFE',
  'LIVE',
  'MAKE',
  'MANY',
  'MORE',
  'MUST',
  'NEED',
  'ONLY',
  'OPEN',
  'OVER',
  'PAID',
  'PASS',
  'PLAN',
  'REAL',
  'SAFE',
  'SAVE',
  'SHOW',
  'SIGN',
  'STAY',
  'STOP',
  'SUCH',
  'TAKE',
  'TELL',
  'THAN',
  'THAT',
  'THEM',
  'THEN',
  'THEY',
  'THIS',
  'TIME',
  'URGE',
  'VETO',
  'VOTE',
  'WANT',
  'WERE',
  'WHAT',
  'WHEN',
  'WITH',
  'WORK',
  'YOUR',
  'ABOUT',
  'AFTER',
  'AGAIN',
  'CLEAN',
  'CLOSE',
  'COURT',
  'EVERY',
  'FIGHT',
  'FUNDS',
  'HANDS',
  'HOUSE',
  'HUMAN',
  'LEAVE',
  'LOCAL',
  'MONEY',
  'NEVER',
  'OTHER',
  'PEACE',
  'POWER',
  'RAISE',
  'RIGHT',
  'SHALL',
  'SHAME',
  'STAND',
  'START',
  'STATE',
  'THEIR',
  'THERE',
  'THESE',
  'THINK',
  'THOSE',
  'TODAY',
  'TRUMP',
  'TRUTH',
  'UNDER',
  'UNITE',
  'UNTIL',
  'VOICE',
  'VOTES',
  'WHERE',
  'WHICH',
  'WHILE',
  'WOMEN',
  'WOULD',
  'WRONG',
  'YOUTH',
]);

// Words kept lowercase in title case, except at the start
const TITLE_CASE_SMALL_WORDS = new Set([
  'a',
  'an',
  'and',
  'at',
  'but',
  'by',
  'for',
  'from',
  'in',
  'nor',
  'of',
  'on',
  'or',
  'the',
  'to',
  'with',
]);

/**
 * Title-case a subject that was typed in (mostly) ALL CAPS, preserving
 * acronym-looking tokens. Subjects with intentional mixed casing pass through
 * untouched.
 */
export const normalizeShoutingSubject = (subject: string = ''): string => {
  const cleaned = collapseWhitespace(subject);
  const letters = cleaned.replace(/[^a-zA-Z]/g, '');
  const uppercase = letters.replace(/[^A-Z]/g, '');

  if (
    !letters ||
    uppercase.length / letters.length < UPPERCASE_SUBJECT_THRESHOLD
  ) {
    return cleaned;
  }

  return cleaned
    .split(' ')
    .map((word, index) => {
      const wordLetters = word.replace(/[^a-zA-Z]/g, '');
      if (
        ACRONYM_PATTERN.test(wordLetters) &&
        !COMMON_SHORT_WORDS.has(wordLetters)
      ) {
        // A possessive tail isn't part of the acronym: DOJ'S → DOJ's
        return word.replace(/(['’])S$/, '$1s');
      }
      const lowered = word.toLowerCase();
      if (index > 0 && TITLE_CASE_SMALL_WORDS.has(wordLetters.toLowerCase())) {
        return lowered;
      }
      // Capitalize after hyphens too, for names like Ocasio-Cortez
      return lowered.replace(/(^|-)[a-z]/g, (m) => m.toUpperCase());
    })
    .join(' ');
};

export interface LetterTitleProps {
  subject?: string;
  recipient?: string;
}

// A token that could plausibly match a search query: an acronym, or at least
// two letters with a vowel in them. A subject with none of these (digits,
// punctuation, a consonant keysmash) makes a worse title than the recipient
// alone.
const isSearchableWord = (word: string): boolean => {
  const wordLetters = word.replace(/[^a-zA-Z]/g, '');
  if (ACRONYM_PATTERN.test(wordLetters)) {
    return true;
  }
  return wordLetters.length >= 2 && /[aeiouy]/i.test(wordLetters);
};

/**
 * Build the SEO title for a letter page (the brand suffix is appended by the
 * caller). Wraps the user-typed subject with context matching how searchers
 * phrase these queries ("<topic> open letter"), shedding the intent tail and
 * then truncating the subject before exceeding the visible-title budget.
 */
export const buildLetterTitle = ({
  subject = '',
  recipient = '',
}: LetterTitleProps = {}): string => {
  const normalizedSubject = normalizeShoutingSubject(subject);
  const cleanedRecipient = collapseWhitespace(recipient);

  if (!normalizedSubject.split(' ').some(isSearchableWord)) {
    return cleanedRecipient
      ? truncateAtWordBoundary(
          `An Open Letter to ${cleanedRecipient}`,
          SEO_TITLE_MAX_LENGTH
        )
      : 'An Open Letter';
  }

  const context = cleanedRecipient
    ? `: Open Letter to ${cleanedRecipient}`
    : ': An Open Letter';

  const full = `${normalizedSubject}${context} — Read & Sign`;
  if (full.length <= SEO_TITLE_MAX_LENGTH) {
    return full;
  }

  const withoutIntent = `${normalizedSubject}${context}`;
  if (withoutIntent.length <= SEO_TITLE_MAX_LENGTH) {
    return withoutIntent;
  }

  // Subject words carry the query terms searchers match on, so when the
  // subject and recipient can't both fit, the recipient is what goes.
  const genericContext = ' — An Open Letter';
  const subjectBudget = SEO_TITLE_MAX_LENGTH - genericContext.length;
  return `${truncateAtWordBoundary(normalizedSubject, subjectBudget)}${genericContext}`;
};

export interface PetitionMetaDescriptionProps {
  subject?: string;
  message?: string;
  fallback?: string;
}

/**
 * Build the meta description for a petition page.
 *
 * Starts at the petition body — the subject already fills the <title>, and
 * repeating it here spends the snippet budget on characters the searcher has
 * already read. Petition bodies frequently open by restating the subject, so
 * that repetition is stripped too. Deliberately omits the signature count — it
 * changes constantly, which churns the snippet, and it spends the most
 * valuable characters on something unsearchable.
 */
export const buildPetitionMetaDescription = ({
  subject = '',
  message = '',
  fallback = '',
}: PetitionMetaDescriptionProps = {}): string => {
  const cleanedSubject = collapseWhitespace(subject);
  let cleanedMessage = collapseWhitespace(message);

  if (
    cleanedSubject &&
    cleanedMessage.toLowerCase().startsWith(cleanedSubject.toLowerCase())
  ) {
    const rest = cleanedMessage.slice(cleanedSubject.length);
    // Only strip at a word boundary so "Stop KOSA" never eats into "KOSAs"
    if (!rest || /^[^a-zA-Z0-9]/.test(rest)) {
      cleanedMessage = rest.replace(/^[\s.,;:!?—–-]+/, '');
    }
  }

  if (cleanedMessage) {
    return truncateAtWordBoundary(cleanedMessage);
  }

  if (cleanedSubject) {
    const lead = /[.!?]$/.test(cleanedSubject)
      ? cleanedSubject
      : `${cleanedSubject}.`;
    return truncateAtWordBoundary(lead);
  }

  return fallback;
};

export interface FormatLetterFieldsProps {
  subject?: string;
  message?: string;
  reps?: string;
}

export interface FormattedLetter {
  title: string;
  description: string;
}

// A letter body that opens with question framing ("Are you calling on…?")
// makes a weak snippet — drop leading question sentences in favor of the
// declarative opening, as long as enough declarative text remains.
const MIN_TEXT_AFTER_QUESTION = 40;

// A question running longer than this isn't opening framing — leave it alone.
// Sized to real letters: the "hegseth resignation" opener is a 201-char
// question.
const MAX_QUESTION_LENGTH = 250;

const LEADING_QUESTION_PATTERN = new RegExp(
  `^[^.!?]{0,${MAX_QUESTION_LENGTH}}\\?\\s+`
);

const stripLeadingRhetoricalQuestions = (text: string): string => {
  let result = text;
  let match = result.match(LEADING_QUESTION_PATTERN);
  while (
    match &&
    result.slice(match[0].length).length >= MIN_TEXT_AFTER_QUESTION
  ) {
    result = result.slice(match[0].length);
    match = result.match(LEADING_QUESTION_PATTERN);
  }
  return result;
};

export const formatLetterFields = ({
  subject = '',
  message = '',
  reps = '',
}: FormatLetterFieldsProps = {}): FormattedLetter => {
  let title = 'An open letter.';
  let description = 'An open letter.';

  // If there is a subject, use that as the title
  // If there is no subject, use the reps preamble as the title
  if (subject) {
    title = normalizeShoutingSubject(subject);
  } else {
    if (reps) {
      title = `An open letter to ${reps}.`;
    }
  }

  // If there is no message, return the remaining defaults. (should be very rare)
  if (!message) {
    return {
      title,
      description,
    };
  }

  description = stripLeadingRhetoricalQuestions(collapseWhitespace(message));

  // Limit to 25 words or 155-160 characters
  const words = description.split(' ');
  if (words.length > 25) {
    description = `${words.slice(0, 25).join(' ')}...`;
  }

  // If description is still longer than 160 characters, limit to 160 characters, append `...`
  if (description.length > 160) {
    description = `${description.substring(0, 160)}...`;
  }

  return {
    title,
    description,
  };
};
